/**
 * Step Requirements Configuration
 *
 * This file defines the access requirements for each step in the tax preparation workflow.
 * Requirements can be backend flags or computed client-side checks.
 *
 * Supports different requirement sets for personal vs business accounts.
 */

// Feature flags for business account behavior
export const BUSINESS_CONFIG = {
  REQUIRE_QUESTIONNAIRE: true,  // Set to true to require questionnaire for businesses
  APPLY_STEP_BLOCKING: true      // Master switch for business step blocking
};

/**
 * Step requirement definitions
 *
 * Structure:
 * - flag: Backend flag name to check (e.g., 'needs_updating')
 * - expected: Expected value for the flag (true/false)
 * - message: User-facing error message if check fails
 * - type: 'computed' for client-side calculations
 * - fn: Function to evaluate computed checks
 */
export const stepRequirements = {
  personal: {
    0: {
      name: 'Confirm Address',
      checks: [] // Always accessible - first step
    },
    1: {
      name: 'Confirm Banking',
      checks: [
        {
          flag: 'needs_updating',
          expected: 0,
          message: 'Please confirm your address information first'
        },
        // ID upload required
        {
          type: 'computed',
          fn: (data) => data?.id?.length > 0,
          message: 'Please upload your ID before proceeding'
        },
        // Spouse ID required when married filing jointly
        {
          type: 'computed',
          fn: (data) => {
            const filingStatus = data?.user?.profile?.filing_status || data?.clientProgress?.filing_status;
            if (filingStatus !== 'Married Filing Joint Return') return true;
            return data?.spouse_id?.length > 0;
          },
          message: 'Please upload your spouse\'s ID before proceeding'
        }
      ]
    },
    2: {
      name: 'Confirm Dependents',
      checks: [
        {
          flag: 'needs_updating',
          expected: 0,
          message: 'Please confirm your address information first'
        },
        // ID upload required
        {
          type: 'computed',
          fn: (data) => data?.id?.length > 0,
          message: 'Please upload your ID before proceeding'
        },
        // Spouse ID required when married filing jointly
        {
          type: 'computed',
          fn: (data) => {
            const filingStatus = data?.user?.profile?.filing_status || data?.clientProgress?.filing_status;
            if (filingStatus !== 'Married Filing Joint Return') return true;
            return data?.spouse_id?.length > 0;
          },
          message: 'Please upload your spouse\'s ID before proceeding'
        },
        {
          flag: 'updated_bank_info',
          expected: 1,
          message: 'Please confirm your banking information first'
        }
      ]
    },
    3: {
      name: 'Tax Questionnaire',
      checks: [
        {
          flag: 'needs_updating',
          expected: 0,
          message: 'Please complete your profile information'
        },
        // ID upload required
        {
          type: 'computed',
          fn: (data) => data?.id?.length > 0,
          message: 'Please upload your ID before proceeding'
        },
        // Spouse ID required when married filing jointly
        {
          type: 'computed',
          fn: (data) => {
            const filingStatus = data?.user?.profile?.filing_status || data?.clientProgress?.filing_status;
            if (filingStatus !== 'Married Filing Joint Return') return true;
            return data?.spouse_id?.length > 0;
          },
          message: 'Please upload your spouse\'s ID before proceeding'
        },
        {
          flag: 'updated_dependents',
          expected: 1,
          message: 'Please confirm your dependents information'
        },
        {
          flag: 'updated_bank_info',
          expected: 1,
          message: 'Please update your banking information'
        }
      ]
    },
    4: {
      name: 'Upload Documents',
      checks: [
        // Check profile requirements first (cascade from Step 3)
        {
          flag: 'needs_updating',
          expected: 0,
          message: 'Please complete your profile information'
        },
        // ID upload required
        {
          type: 'computed',
          fn: (data) => data?.id?.length > 0,
          message: 'Please upload your ID before proceeding'
        },
        // Spouse ID required when married filing jointly
        {
          type: 'computed',
          fn: (data) => {
            const filingStatus = data?.user?.profile?.filing_status || data?.clientProgress?.filing_status;
            if (filingStatus !== 'Married Filing Joint Return') return true;
            return data?.spouse_id?.length > 0;
          },
          message: 'Please upload your spouse\'s ID before proceeding'
        },
        {
          flag: 'updated_dependents',
          expected: 1,
          message: 'Please confirm your dependents information'
        },
        {
          flag: 'updated_bank_info',
          expected: 1,
          message: 'Please update your banking information'
        },
        // Then check questionnaire completion
        {
          type: 'computed',
          fn: (data) => data?.clientProgress?.currentStep >= 3,
          message: 'Please complete the tax questionnaire first'
        }
      ]
    },
    5: {
      name: 'Submit for Filing',
      checks: [
        {
          type: 'computed',
          fn: (data) => data?.finishedCount === data?.documentsCount,
          message: 'Please upload all required documents before filing'
        }
      ]
    }
  },
  business: {
    0: {
      name: 'Confirm Address',
      checks: [] // Always accessible - first step
    },
    1: {
      name: 'Confirm Banking',
      checks: [
        {
          flag: 'needs_updating',
          expected: false,
          message: 'Please confirm your address information first'
        }
      ]
    },
    2: {
      name: 'Confirm Dependents',
      checks: [] // Business accounts don't track dependents - always accessible after Step 1
    },
    3: {
      name: 'Tax Questionnaire',
      checks: [
        {
          flag: 'needs_updating',
          expected: false,
          message: 'Please complete your business profile'
        },
        {
          flag: 'updated_bank_info',
          expected: true,
          message: 'Please update your banking information'
        }
        // Note: No updated_dependents check for businesses (they don't track dependents)
      ]
    },
    4: {
      name: 'Upload Documents',
      checks: [
        // Check profile requirements first (cascade from Step 3)
        {
          flag: 'needs_updating',
          expected: false,
          message: 'Please complete your business profile'
        },
        {
          flag: 'updated_bank_info',
          expected: true,
          message: 'Please update your banking information'
        },
        // Then conditionally require questionnaire based on BUSINESS_CONFIG
        {
          type: 'computed',
          fn: (data) => {
            // If questionnaire not required for businesses, skip this check
            if (!BUSINESS_CONFIG.REQUIRE_QUESTIONNAIRE) return true;
            // Otherwise, require questionnaire to be complete (Step 3)
            return data?.clientProgress?.currentStep >= 3;
          },
          message: 'Please complete the tax questionnaire first'
        }
      ]
    },
    5: {
      name: 'Submit for Filing',
      checks: [
        {
          type: 'computed',
          fn: (data) => data?.finishedCount === data?.documentsCount,
          message: 'Please upload all required documents before filing'
        }
      ]
    }
  }
};

/**
 * Get requirements for a specific account type and step
 * @param {string} accountType - 'personal' or 'business'
 * @param {number} step - Step number (0-3)
 * @returns {Object} Step requirement configuration
 */
export const getStepRequirements = (accountType, step) => {
  const type = accountType === 'business' ? 'business' : 'personal';
  return stepRequirements[type]?.[step] || { name: 'Unknown', checks: [] };
};

/**
 * Check if a specific requirement flag should apply to business accounts
 * @param {string} flag - Flag name to check
 * @returns {boolean} Whether the flag applies to businesses
 */
export const businessRequirementApplies = (flag) => {
  if (!BUSINESS_CONFIG.APPLY_STEP_BLOCKING) return false;

  // Dependents never apply to businesses
  if (flag === 'updated_dependents') return false;

  // All other flags apply
  return true;
};
